import type { PhotoId } from '../gallery/photos';
import type { TextLayer } from '../template';
import { emph, lin, money, rad, solid, step, t, tj, type Box, type SceneRef, type Tx } from './kit';
import { SN, type VideoTemplateDef } from './types';

/** Restaurant menu: Ken Burns on each dish, a price seal and a menu card. */
const menu: VideoTemplateDef = {
  id: 'menu-ken-burns',
  category: 'food',
  format: 'square',
  name: t(
    'Restaurant menu',
    'Меню ресторану',
    'Меню ресторана',
    'Menu restauracji',
    'Meniu de restaurant',
    'Menú del restaurante',
  ),
  description: t(
    'Slow Ken Burns on every dish, price seals and menu cards',
    'Повільний Кен Бернс на кожній страві, цінники-печатки й картки меню',
    'Медленный Кен Бёрнс на каждом блюде, ценники-печати и карточки меню',
    'Powolny Ken Burns na każdym daniu, pieczęcie z ceną i karty menu',
    'Ken Burns lent pe fiecare fel, sigilii cu preț și carduri de meniu',
    'Ken Burns lento en cada plato, sellos de precio y tarjetas de menú',
  ),
  scenes: [
    {
      key: 'intro',
      name: SN.intro,
      duration: 2600,
      transition: { preset: 'crossfade', duration: 600 },
    },
    {
      key: 'dish1',
      name: t('Dish 1', 'Страва 1', 'Блюдо 1', 'Danie 1', 'Fel 1', 'Plato 1'),
      duration: 3000,
      transition: { preset: 'slide-over', duration: 550, params: { direction: 'left' } },
    },
    {
      key: 'dish2',
      name: t('Dish 2', 'Страва 2', 'Блюдо 2', 'Danie 2', 'Fel 2', 'Plato 2'),
      duration: 3000,
      transition: { preset: 'slide-over', duration: 550, params: { direction: 'left' } },
    },
    {
      key: 'dish3',
      name: t('Dish 3', 'Страва 3', 'Блюдо 3', 'Danie 3', 'Fel 3', 'Plato 3'),
      duration: 3000,
      transition: { preset: 'fade-through', duration: 700 },
    },
    { key: 'cta', name: SN.cta, duration: 2800 },
  ],
  build(b) {
    const C = {
      green: '#12372A',
      cream: '#FBF3E4',
      mustard: '#F2B33D',
      tomato: '#E5533D',
      white: '#FFFFFF',
    };
    const F = b.F;
    const display: Partial<TextLayer> = {
      fontFamily: 'Yeseva One',
      fontWeight: 400,
      lineHeight: 1.05,
    };
    const body: Partial<TextLayer> = { fontFamily: 'Manrope', fontWeight: 600 };

    const s0 = b.sc('intro');
    b.photo(
      s0,
      'photo_intro',
      'bakery',
      b.full(),
      { brightness: 0.8 },
      { keys: b.kenBurns(1.04, 1.16, 0.01) },
    );
    b.shape(s0, 'vignette', b.full(), { fill: rad('#12372A33', '#12372AE6') });
    const ic = b.s(0.1);
    b.icon(s0, 'intro_icon', 'utensils', b.centered(b.cx, F.y + F.h * 0.26, ic, ic), C.mustard, {
      enter: step('pop', 0, 500, 'out-back'),
    });
    b.text(
      s0,
      'restaurant',
      'Casa Verde',
      b.in(0, 0.35, 1, 0.22),
      { ...display, fontSize: b.s(0.15), color: C.cream },
      {
        enter: step('letters-blur', 200, 800),
        split: { by: 'char', stagger: 45, order: 'center' },
      },
    );
    b.text(
      s0,
      'tagline',
      t(
        'Wood-fired kitchen since 2012',
        'Кухня на дровах з 2012 року',
        'Кухня на дровах с 2012 года',
        'Kuchnia z pieca opalanego drewnem od 2012',
        'Bucătărie pe foc de lemne din 2012',
        'Cocina de leña desde 2012',
      ),
      b.in(0.05, 0.58, 0.9, 0.1),
      { ...body, fontSize: b.s(0.042), color: C.cream, opacity: 0.9 },
      { enter: step('fade', 900, 500) },
    );
    b.chip(
      s0,
      'menu_tag',
      t('Menu', 'Меню', 'Меню', 'Menu', 'Meniu', 'Menú'),
      { x: b.cx, y: F.y + F.h * 0.72, h: b.s(0.08), align: 'center' },
      { bg: C.mustard, color: C.green, font: 'Manrope', weight: 800, upper: true },
      { enter: step('pop', 1200, 400, 'out-back') },
    );

    const dishes: Array<{
      key: string;
      n: number;
      photo: PhotoId;
      name: Tx;
      desc: Tx;
      price: Tx;
      dx: number;
    }> = [
      {
        key: 'dish1',
        n: 1,
        photo: 'pizza',
        name: t('Margherita', 'Маргарита', 'Маргарита', 'Margherita', 'Margherita', 'Margarita'),
        desc: t(
          'San Marzano tomatoes, fior di latte, basil',
          'Томати Сан-Марцано, фіор-ді-латте, базилік',
          'Томаты Сан-Марцано, фиор-ди-латте, базилик',
          'Pomidory San Marzano, fior di latte, bazylia',
          'Roșii San Marzano, fior di latte, busuioc',
          'Tomate San Marzano, fior di latte, albahaca',
        ),
        price: money({ usd: 12, uah: 349, rub: 690, pln: 39, ron: 45, eur: 11 }),
        dx: 0.02,
      },
      {
        key: 'dish2',
        n: 2,
        photo: 'cake',
        name: t(
          'Raspberry cake',
          'Малиновий торт',
          'Малиновый торт',
          'Tort malinowy',
          'Tort cu zmeură',
          'Tarta de frambuesa',
        ),
        desc: t(
          'Almond sponge, fresh berries, macarons',
          'Мигдальний бісквіт, свіжі ягоди, макарон',
          'Миндальный бисквит, свежие ягоды, макарон',
          'Migdałowy biszkopt, świeże owoce, makaroniki',
          'Blat cu migdale, fructe proaspete, macarons',
          'Bizcocho de almendra, frutos rojos, macarons',
        ),
        price: money({ usd: 7, uah: 199, rub: 390, pln: 25, ron: 29, eur: 7 }),
        dx: -0.02,
      },
      {
        key: 'dish3',
        n: 3,
        photo: 'smoothie',
        name: t(
          'Berry smoothie',
          'Ягідний смузі',
          'Ягодный смузи',
          'Smoothie jagodowe',
          'Smoothie de fructe',
          'Smoothie de frutos rojos',
        ),
        desc: t(
          'Strawberry, raspberry, fresh mint',
          'Полуниця, малина, свіжа м’ята',
          'Клубника, малина, свежая мята',
          'Truskawka, malina, świeża mięta',
          'Căpșuni, zmeură, mentă proaspătă',
          'Fresa, frambuesa y menta fresca',
        ),
        price: money({ usd: 6, uah: 159, rub: 320, pln: 19, ron: 24, eur: 6 }),
        dx: 0.02,
      },
    ];
    for (const d of dishes) {
      const sc = b.sc(d.key);
      b.photo(
        sc,
        `photo_dish_${d.n}`,
        d.photo,
        b.full(),
        {},
        { keys: b.kenBurns(1.03, 1.15, d.dx) },
      );
      b.chip(
        sc,
        `dish_number_${d.n}`,
        `0${d.n} / 03`,
        { x: F.x, y: F.y, h: b.s(0.07) },
        { bg: '#12372AD9', color: C.cream, font: 'Manrope', weight: 700 },
        { enter: step('fade', 200, 400) },
      );
      const card = b.pick<Box>({
        tall: b.in(0, 0.7, 1, 0.3),
        portrait: b.in(0, 0.68, 0.9, 0.32),
        square: b.in(0, 0.64, 0.9, 0.36),
        wide: b.in(0, 0.52, 0.48, 0.48),
      });
      b.shape(
        sc,
        `card_${d.n}`,
        card,
        {
          fill: solid(C.cream),
          borderRadius: b.s(0.03),
          shadow: { x: 0, y: b.s(0.01), blur: b.s(0.04), color: '#00000040' },
        },
        { enter: step('rise', 200, 600, 'out-expo') },
      );
      const pad = b.s(0.04);
      const seal = b.s(0.2);
      b.text(
        sc,
        `dish_name_${d.n}`,
        d.name,
        {
          x: card.x + pad,
          y: card.y + pad * 0.8,
          w: card.w - pad * 2 - seal * 0.6,
          h: card.h * 0.42,
        },
        { ...display, fontSize: b.s(0.08), color: C.green, align: 'left' },
        { enter: step('lines-mask', 450, 600), split: { by: 'line', stagger: 100 } },
      );
      b.text(
        sc,
        `dish_desc_${d.n}`,
        d.desc,
        { x: card.x + pad, y: card.y + card.h * 0.52, w: card.w - pad * 2, h: card.h * 0.36 },
        {
          ...body,
          fontSize: b.s(0.038),
          color: C.green,
          align: 'left',
          lineHeight: 1.3,
          opacity: 0.8,
        },
        { enter: step('fade', 750, 450) },
      );
      const sealBox: Box = {
        x: card.x + card.w - seal,
        y: Math.max(F.y, card.y - seal * 0.6),
        w: seal,
        h: seal,
      };
      b.shape(
        sc,
        `price_seal_${d.n}`,
        sealBox,
        { shape: 'star', points: 16, innerRatio: 0.88, fill: solid(C.mustard), rotation: -8 },
        { enter: step('pop', 900, 500, 'spring-bouncy'), emphasis: emph('spin', 1400, 6000, 1) },
      );
      b.text(
        sc,
        `price_${d.n}`,
        d.price,
        { x: sealBox.x + seal * 0.14, y: sealBox.y + seal * 0.14, w: seal * 0.72, h: seal * 0.72 },
        { ...display, fontSize: b.s(0.055), color: C.green, rotation: -8 },
        { enter: step('pop', 1000, 450, 'spring-bouncy'), emphasis: emph('tada', 1700, 800) },
      );
    }

    const cta = b.sc('cta');
    b.backdrop(cta, 'cta_bg', solid(C.green));
    const ph = b.pick({
      tall: F.w * 0.42,
      portrait: F.h * 0.3,
      square: F.h * 0.3,
      wide: F.h * 0.36,
    });
    // Side plates first, the middle one on top
    const thumbs: Array<[PhotoId, number]> = [
      ['pizza', 0],
      ['smoothie', 2],
      ['cake', 1],
    ];
    thumbs.forEach(([p, i]) =>
      b.photo(
        cta,
        `cta_dish_${i + 1}`,
        p,
        b.centered(
          b.cx + (i - 1) * ph * 0.78,
          F.y + F.h * (b.tall ? 0.18 : 0.17),
          ph * (i === 1 ? 0.9 : 0.72),
          ph * (i === 1 ? 0.9 : 0.72),
        ),
        { borderRadius: ph, borderWidth: b.s(0.008), borderColor: C.cream },
        { enter: step('pop', [150, 0, 300][i], 500, 'out-back') },
      ),
    );
    b.text(
      cta,
      'cta_headline',
      t(
        'Order delivery',
        'Замовте доставку',
        'Закажите доставку',
        'Zamów z dostawą',
        'Comandă cu livrare',
        'Pide a domicilio',
      ),
      b.in(0, b.tall ? 0.36 : 0.36, 1, 0.18),
      { ...display, fontSize: b.s(0.1), color: C.cream },
      { enter: step('words-rise', 300, 550), split: { by: 'word', stagger: 90 } },
    );
    const facts: Array<{ icon: string; text: Tx }> = [
      { icon: 'clock', text: t('30 min', '30 хв', '30 мин', '30 min', '30 min', '30 min') },
      {
        icon: 'truck',
        text: tj(
          t(
            'free from ',
            'безкоштовно від ',
            'бесплатно от ',
            'gratis od ',
            'gratuit de la ',
            'gratis desde ',
          ),
          money({ usd: 25, uah: 600, rub: 1200, pln: 80, ron: 100, eur: 25 }),
        ),
      },
    ];
    const fy = F.y + F.h * 0.58;
    const fh = b.s(0.075);
    const fw = Math.min(F.w * 0.48, b.s(0.46));
    facts.forEach((f, i) => {
      const x = b.cx + (i === 0 ? -fw - b.s(0.01) : b.s(0.01));
      const boxW = i === 0 ? fw : fw;
      b.shape(
        cta,
        `fact_bg_${i + 1}`,
        { x, y: fy, w: boxW, h: fh },
        { fill: solid('#FBF3E41F'), borderRadius: fh / 2 },
        { enter: step('fade', 600 + i * 150, 400) },
      );
      b.icon(
        cta,
        `fact_icon_${i + 1}`,
        f.icon,
        { x: x + fh * 0.3, y: fy + fh * 0.22, w: fh * 0.56, h: fh * 0.56 },
        C.mustard,
        {
          enter: step('pop', 650 + i * 150, 400, 'out-back'),
        },
      );
      b.text(
        cta,
        `fact_${i + 1}`,
        f.text,
        { x: x + fh, y: fy, w: boxW - fh * 1.2, h: fh },
        {
          ...body,
          fontWeight: 700,
          fontSize: b.s(0.034),
          color: C.cream,
          align: 'left',
          maxLines: 1,
        },
        { enter: step('fade', 650 + i * 150, 400) },
      );
    });
    b.chip(
      cta,
      'cta',
      t('Order now', 'Замовити', 'Заказать', 'Zamów teraz', 'Comandă acum', 'Pedir ahora'),
      { x: b.cx, y: F.y + F.h * 0.72, h: b.s(0.11), align: 'center' },
      { bg: C.mustard, color: C.green, font: 'Manrope', weight: 800 },
      { enter: step('pop', 1000, 450, 'out-back'), emphasis: emph('pulse', 1500, 1200, 0) },
    );
    b.text(
      cta,
      'site',
      'casaverde.menu',
      b.in(0, 0.88, 1, 0.08),
      { ...body, fontSize: b.s(0.04), color: C.cream, opacity: 0.8 },
      { enter: step('fade', 1200, 400) },
    );
    return solid(C.green);
  },
};

/** Real-estate tour: slow push-ins through the rooms with info chips. */
const propertyTour: VideoTemplateDef = {
  id: 'property-tour',
  category: 'realestate',
  format: 'wide',
  name: t(
    'Property tour',
    'Тур по об’єкту',
    'Тур по объекту',
    'Wirtualny spacer',
    'Tur de proprietate',
    'Tour por la vivienda',
  ),
  description: t(
    'Slow push-ins through the rooms, info chips and a price card',
    'Повільні наїзди кімнатами, інфочипи й картка з ціною',
    'Медленные наезды по комнатам, инфочипы и карточка с ценой',
    'Powolne najazdy po pokojach, etykiety z informacjami i cena',
    'Apropieri lente prin camere, etichete cu detalii și preț',
    'Acercamientos lentos por las estancias, etiquetas y precio',
  ),
  scenes: [
    {
      key: 'exterior',
      name: t('Exterior', 'Фасад', 'Фасад', 'Budynek', 'Exterior', 'Exterior'),
      duration: 3200,
      transition: { preset: 'crossfade', duration: 800 },
    },
    {
      key: 'living',
      name: t('Interior', 'Інтер’єр', 'Интерьер', 'Wnętrze', 'Interior', 'Interior'),
      duration: 3200,
      transition: { preset: 'blur', duration: 700 },
    },
    {
      key: 'details',
      name: SN.details,
      duration: 3200,
      transition: { preset: 'slide-over', duration: 600, params: { direction: 'left' } },
    },
    {
      key: 'price',
      name: t('Price', 'Ціна', 'Цена', 'Cena', 'Preț', 'Precio'),
      duration: 3000,
      transition: { preset: 'fade-through', duration: 700 },
    },
    { key: 'cta', name: SN.cta, duration: 2600 },
  ],
  build(b) {
    const C = { navy: '#0F1B2D', gold: '#C9A45C', white: '#FFFFFF', mist: '#E8ECF2' };
    const F = b.F;
    const caps = (size: number, color: string): Partial<TextLayer> => ({
      fontFamily: 'Tenor Sans',
      fontWeight: 400,
      fontSize: size,
      color,
      textTransform: 'uppercase',
      letterSpacing: Math.round(size * 0.12),
      lineHeight: 1.1,
    });
    const body: Partial<TextLayer> = { fontFamily: 'Manrope', fontWeight: 600 };
    const sideShade = (sc: SceneRef, name: string) =>
      b.wide
        ? b.shade(
            sc,
            name,
            b.full(),
            [
              [0, '#0F1B2DE6'],
              [0.45, '#0F1B2D80'],
              [0.75, '#0F1B2D00'],
            ],
            90,
          )
        : b.shade(sc, name, b.full(), [
            [0, '#0F1B2D00'],
            [0.3, '#0F1B2D1A'],
            [0.5, '#0F1B2DA6'],
            [1, '#0F1B2DF2'],
          ]);
    const textCol = b.wide
      ? b.in(0, 0.3, 0.46, 0.62)
      : b.in(0, b.tall ? 0.56 : 0.5, 1, b.tall ? 0.44 : 0.5);

    const s0 = b.sc('exterior');
    b.photo(s0, 'photo_exterior', 'apartments', b.full(), {}, { keys: b.kenBurns(1, 1.12) });
    sideShade(s0, 'shade_exterior');
    b.text(
      s0,
      'label',
      t(
        'New build · 2026',
        'Новобудова · 2026',
        'Новостройка · 2026',
        'Nowa inwestycja · 2026',
        'Construcție nouă · 2026',
        'Obra nueva · 2026',
      ),
      { x: textCol.x, y: textCol.y, w: textCol.w, h: textCol.h * 0.12 },
      { ...caps(b.s(0.03), C.gold), align: 'left' },
      { enter: step('fade', 300, 500) },
    );
    b.text(
      s0,
      'headline',
      t(
        'Riverside Residence',
        'Riverside Residence',
        'Riverside Residence',
        'Riverside Residence',
        'Riverside Residence',
        'Riverside Residence',
      ),
      { x: textCol.x, y: textCol.y + textCol.h * 0.14, w: textCol.w, h: textCol.h * 0.5 },
      { ...caps(b.s(0.085), C.white), align: 'left', letterSpacing: b.s(0.004) },
      { enter: step('lines-mask', 500, 800), split: { by: 'line', stagger: 150 } },
    );
    const pin = b.s(0.055);
    const ay = textCol.y + textCol.h * 0.72;
    b.icon(s0, 'address_icon', 'map-pin', { x: textCol.x, y: ay, w: pin, h: pin }, C.gold, {
      enter: step('drop', 1100, 600, 'bounce'),
    });
    b.text(
      s0,
      'address',
      t(
        '12 River Street',
        'вул. Річкова, 12',
        'ул. Речная, 12',
        'ul. Rzeczna 12',
        'Str. Râului 12',
        'Calle del Río 12',
      ),
      { x: textCol.x + pin * 1.3, y: ay, w: textCol.w - pin * 1.3, h: pin },
      { ...body, fontSize: b.s(0.04), color: C.white, align: 'left' },
      { enter: step('slide', 1200, 500, 'out-expo', { direction: 'right', distance: 15 }) },
    );

    const s1 = b.sc('living');
    b.photo(
      s1,
      'photo_living',
      'window-view',
      b.full(),
      { brightness: 1.1 },
      { keys: b.kenBurns(1.04, 1.14, -0.02) },
    );
    sideShade(s1, 'shade_living');
    b.text(
      s1,
      'living_title',
      t(
        'Bright & spacious',
        'Світло й простір',
        'Свет и простор',
        'Jasno i przestronnie',
        'Luminos și spațios',
        'Luminoso y amplio',
      ),
      { x: textCol.x, y: textCol.y, w: textCol.w, h: textCol.h * 0.26 },
      { ...caps(b.s(0.06), C.white), align: 'left', letterSpacing: b.s(0.003) },
      { enter: step('lines-mask', 200, 700), split: { by: 'line', stagger: 120 } },
    );
    const facts: Array<{ icon: string; text: Tx }> = [
      {
        icon: 'house',
        text: t('3 rooms', '3 кімнати', '3 комнаты', '3 pokoje', '3 camere', '3 habitaciones'),
      },
      {
        icon: 'building-2',
        text: t(
          '84 m² · floor 7',
          '84 м² · 7 поверх',
          '84 м² · 7 этаж',
          '84 m² · 7 piętro',
          '84 m² · etajul 7',
          '84 m² · planta 7',
        ),
      },
      {
        icon: 'sun',
        text: t(
          'South-facing windows',
          'Вікна на південь',
          'Окна на юг',
          'Okna na południe',
          'Ferestre spre sud',
          'Ventanas al sur',
        ),
      },
    ];
    const ch = Math.min(b.s(0.085), (textCol.h * 0.66) / 3.3);
    const cw = Math.min(textCol.w, b.s(0.62));
    facts.forEach((f, i) => {
      const y = textCol.y + textCol.h * 0.32 + i * (ch + b.s(0.02));
      b.shape(
        s1,
        `fact_bg_${i + 1}`,
        { x: textCol.x, y, w: cw, h: ch },
        {
          fill: solid('#0F1B2DB3'),
          borderRadius: b.s(0.015),
          strokeWidth: 1,
          strokeColor: '#C9A45C80',
        },
        {
          enter: step('slide', 600 + i * 180, 550, 'out-expo', {
            direction: 'right',
            distance: 20,
          }),
        },
      );
      b.icon(
        s1,
        `fact_icon_${i + 1}`,
        f.icon,
        { x: textCol.x + ch * 0.25, y: y + ch * 0.22, w: ch * 0.56, h: ch * 0.56 },
        C.gold,
        {
          enter: step('pop', 750 + i * 180, 400, 'out-back'),
        },
      );
      b.text(
        s1,
        `fact_${i + 1}`,
        f.text,
        { x: textCol.x + ch, y, w: cw - ch * 1.15, h: ch },
        { ...body, fontSize: b.s(0.04), color: C.white, align: 'left', maxLines: 1 },
        { enter: step('fade', 750 + i * 180, 400) },
      );
    });

    const s2 = b.sc('details');
    b.backdrop(s2, 'details_bg', solid(C.navy));
    const photo: Box = b.upright
      ? { x: 0, y: 0, w: b.W, h: b.tall ? F.y + F.h * 0.5 : b.H * 0.5 }
      : { x: 0, y: 0, w: b.W * (b.wide ? 0.55 : 0.42), h: b.H };
    b.photo(s2, 'photo_details', 'cozy-house', photo, {}, { keys: b.kenBurns(1.02, 1.12, 0.01) });
    const list: Box = b.upright
      ? { x: F.x, y: photo.h + b.s(0.05), w: F.w, h: F.y + F.h - photo.h - b.s(0.05) }
      : {
          x: photo.w + b.s(0.06),
          y: F.y + F.h * 0.12,
          w: F.x + F.w - photo.w - b.s(0.06),
          h: F.h * 0.76,
        };
    b.text(
      s2,
      'details_title',
      t(
        'Life around you',
        'Усе поруч',
        'Всё рядом',
        'Wszystko blisko',
        'Totul aproape',
        'Todo a mano',
      ),
      { x: list.x, y: list.y, w: list.w, h: list.h * 0.22 },
      { ...caps(b.s(0.06), C.white), align: 'left', letterSpacing: b.s(0.003) },
      { enter: step('lines-mask', 300, 600), split: { by: 'line', stagger: 100 } },
    );
    const items: Tx[] = [
      t(
        'Private terrace',
        'Власна тераса',
        'Своя терраса',
        'Prywatny taras',
        'Terasă privată',
        'Terraza privada',
      ),
      t(
        'Underground parking',
        'Підземний паркінг',
        'Подземный паркинг',
        'Parking podziemny',
        'Parcare subterană',
        'Parking subterráneo',
      ),
      t(
        '5 min to the metro',
        '5 хв до метро',
        '5 минут до метро',
        '5 min do metra',
        '5 min până la metrou',
        'Metro a 5 minutos',
      ),
    ];
    const rowH = (list.h * 0.7) / 3;
    items.forEach((it, i) => {
      const y = list.y + list.h * 0.28 + i * rowH;
      b.shape(
        s2,
        `detail_line_${i + 1}`,
        { x: list.x, y: y + rowH / 2 - 1, w: b.s(0.05), h: Math.max(2, b.s(0.004)) },
        { fill: solid(C.gold) },
        { enter: step('wipe', 500 + i * 180, 400, 'out-expo', { direction: 'right' }) },
      );
      b.text(
        s2,
        `detail_${i + 1}`,
        it,
        { x: list.x + b.s(0.075), y, w: list.w - b.s(0.075), h: rowH },
        { ...body, fontSize: b.s(0.045), color: C.mist, align: 'left' },
        {
          enter: step('slide', 550 + i * 180, 500, 'out-expo', {
            direction: 'right',
            distance: 10,
          }),
        },
      );
    });

    const s3 = b.sc('price');
    b.backdrop(s3, 'price_bg', lin(135, C.navy, '#1B2D4A'));
    const fr = b.pick<Box>({
      tall: b.in(0.08, 0, 0.84, 0.42),
      portrait: b.in(0.15, 0, 0.7, 0.42),
      square: b.in(0.2, 0, 0.6, 0.4),
      wide: b.in(0, 0.06, 0.42, 0.88),
    });
    const inset = b.s(0.02);
    b.shape(
      s3,
      'price_frame',
      fr,
      { fill: null, strokeWidth: Math.max(2, b.s(0.003)), strokeColor: C.gold },
      { enter: step('zoom-out', 0, 900, 'out-expo') },
    );
    b.photo(
      s3,
      'photo_price',
      'apartments',
      { x: fr.x + inset, y: fr.y + inset, w: fr.w - inset * 2, h: fr.h - inset * 2 },
      { focusX: 0.6 },
      { enter: step('fade', 150, 700), keys: b.kenBurns(1, 1.08) },
    );
    const pc = b.wide ? b.in(0.48, 0.18, 0.52, 0.64) : b.in(0, 0.5, 1, 0.48);
    const al = b.wide ? 'left' : 'center';
    b.text(
      s3,
      'price_label',
      t(
        'Apartments from',
        'Квартири від',
        'Квартиры от',
        'Mieszkania od',
        'Apartamente de la',
        'Pisos desde',
      ),
      { x: pc.x, y: pc.y, w: pc.w, h: pc.h * 0.18 },
      { ...caps(b.s(0.034), C.gold), align: al },
      { enter: step('fade', 400, 500) },
    );
    b.text(
      s3,
      'price',
      money({ usd: 240000, uah: 9800000, rub: 21500000, pln: 960000, ron: 1190000, eur: 225000 }),
      { x: pc.x, y: pc.y + pc.h * 0.2, w: pc.w, h: pc.h * 0.36 },
      {
        fontFamily: 'Tenor Sans',
        fontWeight: 400,
        fontSize: b.s(0.12),
        color: C.white,
        align: al,
        maxLines: 1,
      },
      { enter: step('blur-in', 600, 700), emphasis: emph('shine', 1500, 900) },
    );
    b.text(
      s3,
      'mortgage',
      t(
        'Mortgage from 6.9% · 0% down',
        'Іпотека від 6,9% · без внеску',
        'Ипотека от 6,9% · без взноса',
        'Kredyt od 6,9% · bez wkładu',
        'Credit de la 6,9% · fără avans',
        'Hipoteca desde 6,9% · sin entrada',
      ),
      { x: pc.x, y: pc.y + pc.h * 0.62, w: pc.w, h: pc.h * 0.16 },
      { ...body, fontSize: b.s(0.042), color: C.mist, align: al },
      { enter: step('rise', 900, 500, 'out-expo') },
    );

    const s4 = b.sc('cta');
    b.photo(
      s4,
      'photo_cta',
      'apartments',
      b.full(),
      { blur: 6, brightness: 0.5 },
      { keys: b.kenBurns(1.1, 1.16) },
    );
    b.shape(s4, 'cta_tint', b.full(), { fill: solid('#0F1B2DB3') });
    const kc = b.s(0.1);
    b.icon(s4, 'cta_icon', 'key', b.centered(b.cx, F.y + F.h * 0.22, kc, kc), C.gold, {
      enter: step('spin', 0, 700, 'out-back'),
    });
    b.text(
      s4,
      'cta_headline',
      t(
        'Book a private viewing',
        'Запишіться на перегляд',
        'Запишитесь на просмотр',
        'Umów prywatną prezentację',
        'Programează o vizionare',
        'Reserva una visita privada',
      ),
      b.in(0.05, 0.32, 0.9, 0.24),
      { ...caps(b.s(0.07), C.white), letterSpacing: b.s(0.003) },
      { enter: step('lines-mask', 200, 700), split: { by: 'line', stagger: 120 } },
    );
    b.chip(
      s4,
      'cta',
      t('Book a viewing', 'Записатися', 'Записаться', 'Umów się', 'Programează-te', 'Reservar'),
      { x: b.cx, y: F.y + F.h * 0.62, h: b.s(0.1), align: 'center' },
      { bg: C.gold, color: C.navy, font: 'Manrope', weight: 800, upper: true, radius: b.s(0.012) },
      { enter: step('rise', 700, 500, 'out-expo') },
    );
    b.text(
      s4,
      'phone',
      '+1 555 014 2200 · riverside-homes.com',
      b.in(0, 0.8, 1, 0.08),
      { ...body, fontSize: b.s(0.036), color: C.mist },
      { enter: step('fade', 1000, 400) },
    );
    return solid(C.navy);
  },
};

/** Cinematic travel promo: letterbox, spaced titles, slow pans, dips to black. */
const cinematicTravel: VideoTemplateDef = {
  id: 'cinematic-travel',
  category: 'travel',
  format: 'wide',
  name: t(
    'Cinematic travel',
    'Кіношна подорож',
    'Киношное путешествие',
    'Filmowa podróż',
    'Călătorie cinematică',
    'Viaje cinematográfico',
  ),
  description: t(
    'Letterbox, spaced serif titles, slow pans and dips to black',
    'Кінорамка, розріджені титри, повільні панорами й затемнення',
    'Кинорамка, разреженные титры, медленные панорамы и затемнения',
    'Kadr kinowy, eleganckie napisy, wolne panoramy i ściemnienia',
    'Benzi cinema, titluri elegante, panoramări lente și fade-uri',
    'Bandas de cine, títulos elegantes, paneos lentos y fundidos',
  ),
  scenes: [
    {
      key: 'open',
      name: SN.intro,
      duration: 3200,
      transition: { preset: 'fade-through', duration: 800 },
    },
    {
      key: 'dest1',
      name: t('Venice', 'Венеція', 'Венеция', 'Wenecja', 'Veneția', 'Venecia'),
      duration: 2800,
      transition: { preset: 'crossfade', duration: 700 },
    },
    {
      key: 'dest2',
      name: t('Cappadocia', 'Каппадокія', 'Каппадокия', 'Kapadocja', 'Cappadocia', 'Capadocia'),
      duration: 2800,
      transition: { preset: 'crossfade', duration: 700 },
    },
    {
      key: 'dest3',
      name: t(
        'Cinque Terre',
        'Чинкве-Терре',
        'Чинкве-Терре',
        'Cinque Terre',
        'Cinque Terre',
        'Cinque Terre',
      ),
      duration: 2800,
      transition: { preset: 'blur', duration: 700 },
    },
    {
      key: 'offer',
      name: SN.offer,
      duration: 3200,
      transition: { preset: 'fade-through', duration: 700 },
    },
    { key: 'cta', name: SN.cta, duration: 2800 },
  ],
  build(b) {
    const C = { night: '#120E16', gold: '#F6C177', cream: '#FFF4E6', white: '#FFFFFF' };
    const F = b.F;
    // Text keeps clear of the letterbox bars (9% of the height)
    const bar = 0.09;
    const top = Math.max(F.y, b.H * (bar + 0.03));
    const bottom = Math.min(F.y + F.h, b.H * (1 - bar - 0.03));
    const area: Box = { x: F.x, y: top, w: F.w, h: bottom - top };
    const at = (fx: number, fy: number, fw: number, fh: number): Box => ({
      x: area.x + area.w * fx,
      y: area.y + area.h * fy,
      w: area.w * fw,
      h: area.h * fh,
    });
    const serif: Partial<TextLayer> = {
      fontFamily: 'Cormorant Garamond',
      fontWeight: 600,
      fontStyle: 'italic',
      lineHeight: 1,
      color: C.cream,
      shadow: { x: 0, y: b.s(0.003), blur: b.s(0.025), color: '#00000099' },
    };
    const caps = (size: number, color: string): Partial<TextLayer> => ({
      fontFamily: 'Tenor Sans',
      fontWeight: 400,
      fontSize: size,
      color,
      textTransform: 'uppercase',
      letterSpacing: Math.round(size * 0.35),
      shadow: { x: 0, y: 1, blur: b.s(0.012), color: '#000000B3' },
    });
    const dim = (sc: SceneRef, name: string) =>
      b.shade(sc, name, b.full(), [
        [0, '#120E1666'],
        [0.4, '#120E1600'],
        [0.65, '#120E1633'],
        [1, '#120E16CC'],
      ]);

    const s0 = b.sc('open');
    b.photo(
      s0,
      'photo_open',
      'santorini',
      b.full(),
      { brightness: 0.8 },
      { keys: b.kenBurns(1.16, 1.02) },
    );
    b.shape(s0, 'shade_open', b.full(), { fill: rad('#120E1666', '#120E16B3') });
    b.text(
      s0,
      'kicker',
      t(
        '2026 collection',
        'Колекція 2026',
        'Коллекция 2026',
        'Kolekcja 2026',
        'Colecția 2026',
        'Colección 2026',
      ),
      at(0, 0.3, 1, 0.08),
      caps(b.s(0.03), C.gold),
      { enter: step('fade', 300, 800) },
    );
    b.text(
      s0,
      'headline',
      t(
        'Summer escapes',
        'Літні втечі',
        'Летние побеги',
        'Letnie ucieczki',
        'Evadări de vară',
        'Escapadas de verano',
      ),
      at(0, 0.4, 1, 0.26),
      { ...serif, fontSize: b.s(0.16) },
      {
        enter: step('letters-blur', 600, 900),
        split: { by: 'char', stagger: 40, order: 'center' },
      },
    );
    b.shape(
      s0,
      'headline_rule',
      b.centered(b.cx, area.y + area.h * 0.72, b.s(0.16), Math.max(2, b.s(0.003))),
      { fill: solid(C.gold) },
      { enter: step('unfold', 1400, 700, 'out-expo', { direction: 'right' }) },
    );

    const dests: Array<{
      key: string;
      n: string;
      photo: PhotoId;
      name: Tx;
      sub: Tx;
      dx: number;
      dy: number;
    }> = [
      {
        key: 'dest1',
        n: '01',
        photo: 'venice',
        name: t('Venice', 'Венеція', 'Венеция', 'Wenecja', 'Veneția', 'Venecia'),
        sub: t(
          'Italy · 4 nights',
          'Італія · 4 ночі',
          'Италия · 4 ночи',
          'Włochy · 4 noce',
          'Italia · 4 nopți',
          'Italia · 4 noches',
        ),
        dx: 0.03,
        dy: 0,
      },
      {
        key: 'dest2',
        n: '02',
        photo: 'balloons',
        name: t('Cappadocia', 'Каппадокія', 'Каппадокия', 'Kapadocja', 'Cappadocia', 'Capadocia'),
        sub: t(
          'Türkiye · 5 nights',
          'Туреччина · 5 ночей',
          'Турция · 5 ночей',
          'Turcja · 5 nocy',
          'Turcia · 5 nopți',
          'Turquía · 5 noches',
        ),
        dx: -0.03,
        dy: 0.01,
      },
      {
        key: 'dest3',
        n: '03',
        photo: 'seaside-town',
        name: t(
          'Cinque Terre',
          'Чинкве-Терре',
          'Чинкве-Терре',
          'Cinque Terre',
          'Cinque Terre',
          'Cinque Terre',
        ),
        sub: t(
          'Italy · 3 nights',
          'Італія · 3 ночі',
          'Италия · 3 ночи',
          'Włochy · 3 noce',
          'Italia · 3 nopți',
          'Italia · 3 noches',
        ),
        dx: 0.02,
        dy: -0.01,
      },
    ];
    for (const d of dests) {
      const sc = b.sc(d.key);
      b.photo(
        sc,
        `photo_${d.key}`,
        d.photo,
        b.full(),
        {},
        { keys: b.kenBurns(1.06, 1.16, d.dx, d.dy) },
      );
      dim(sc, `shade_${d.key}`);
      const box = at(0, 0.56, b.wide ? 0.6 : 1, 0.44);
      b.text(
        sc,
        `index_${d.key}`,
        d.n,
        { x: box.x, y: box.y, w: box.w, h: box.h * 0.2 },
        { ...caps(b.s(0.032), C.gold), align: 'left' },
        { enter: step('fade', 200, 500) },
      );
      b.text(
        sc,
        `name_${d.key}`,
        d.name,
        { x: box.x, y: box.y + box.h * 0.2, w: box.w, h: box.h * 0.52 },
        { ...serif, fontSize: b.s(0.13), align: 'left' },
        { enter: step('lines-mask', 350, 900), split: { by: 'line', stagger: 120 } },
      );
      b.text(
        sc,
        `sub_${d.key}`,
        d.sub,
        { x: box.x, y: box.y + box.h * 0.76, w: box.w, h: box.h * 0.2 },
        { ...caps(b.s(0.028), C.cream), align: 'left' },
        { enter: step('drift', 800, 900, 'out-quart', { direction: 'right', distance: 6 }) },
      );
    }

    const s4 = b.sc('offer');
    b.photo(s4, 'photo_offer', 'palm-sunset', b.full(), {}, { keys: b.kenBurns(1.02, 1.12) });
    b.shape(s4, 'offer_tint', b.full(), { fill: rad('#120E1633', '#120E16B3') });
    b.text(
      s4,
      'offer_kicker',
      t(
        '7 nights · flights + hotel',
        '7 ночей · переліт + готель',
        '7 ночей · перелёт + отель',
        '7 nocy · lot + hotel',
        '7 nopți · zbor + hotel',
        '7 noches · vuelo + hotel',
      ),
      at(0, 0.24, 1, 0.1),
      caps(b.s(0.03), C.cream),
      { enter: step('fade', 200, 700) },
    );
    b.text(
      s4,
      'offer_price',
      tj(
        t('from ', 'від ', 'от ', 'od ', 'de la ', 'desde '),
        money({ usd: 799, uah: 32900, rub: 69900, pln: 3199, ron: 3990, eur: 749 }),
      ),
      at(0, 0.36, 1, 0.3),
      { ...serif, fontSize: b.s(0.17), color: C.gold, maxLines: 1 },
      { enter: step('blur-in', 500, 900), emphasis: emph('pulse', 1500, 1600, 1) },
    );
    b.text(
      s4,
      'offer_note',
      t(
        'Early booking until May 31',
        'Раннє бронювання до 31 травня',
        'Раннее бронирование до 31 мая',
        'Wczesna rezerwacja do 31 maja',
        'Rezervare timpurie până pe 31 mai',
        'Reserva anticipada hasta el 31 de mayo',
      ),
      at(0.1, 0.72, 0.8, 0.1),
      { fontFamily: 'Tenor Sans', fontWeight: 400, fontSize: b.s(0.034), color: C.cream },
      { enter: step('fade', 1200, 600) },
    );

    const s5 = b.sc('cta');
    b.photo(
      s5,
      'photo_cta',
      'mountain-lake',
      b.full(),
      { brightness: 0.6 },
      { keys: b.kenBurns(1.04, 1.12, 0, -0.01) },
    );
    b.shape(s5, 'cta_tint', b.full(), { fill: solid('#120E1659') });
    b.text(
      s5,
      'cta_headline',
      t(
        'Your summer starts here',
        'Твоє літо починається тут',
        'Твоё лето начинается здесь',
        'Twoje lato zaczyna się tutaj',
        'Vara ta începe aici',
        'Tu verano empieza aquí',
      ),
      at(0.05, 0.2, 0.9, 0.36),
      { ...serif, fontSize: b.s(0.11) },
      { enter: step('lines-mask', 200, 900), split: { by: 'line', stagger: 150 } },
    );
    b.chip(
      s5,
      'cta',
      t(
        'Book your escape',
        'Забронювати',
        'Забронировать',
        'Zarezerwuj',
        'Rezervă acum',
        'Reserva ya',
      ),
      { x: b.cx, y: area.y + area.h * 0.62, h: b.s(0.095), align: 'center' },
      {
        bg: C.cream,
        color: C.night,
        font: 'Tenor Sans',
        weight: 400,
        upper: true,
        extra: { letterSpacing: b.s(0.008) },
      },
      { enter: step('fade', 900, 600) },
    );
    b.text(s5, 'site', 'wanderly.travel', at(0, 0.82, 1, 0.1), caps(b.s(0.026), C.gold), {
      enter: step('fade', 1200, 500),
    });
    b.addFx(null, 'letterbox', 0, b.duration, { mode: 'hold', size: bar * 100, color: '#000000' });
    return solid(C.night);
  },
};

/** Fitness promo: glitch hits, camera shake, slices and neon on black. */
const fitnessHype: VideoTemplateDef = {
  id: 'fitness-hype',
  category: 'fitness',
  format: 'vertical',
  name: t(
    'Fitness hype',
    'Фітнес-драйв',
    'Фитнес-драйв',
    'Fitness hype',
    'Fitness energic',
    'Fitness a tope',
  ),
  description: t(
    'Glitch hits, camera shake, slices and neon on black',
    'Глітч-удари, тряска камери, нарізка й неон на чорному',
    'Глитч-удары, тряска камеры, нарезка и неон на чёрном',
    'Glitche, drgania kamery, cięcia i neon na czerni',
    'Glitch, cameră care tremură, felii și neon pe negru',
    'Glitch, cámara temblorosa, cortes y neón sobre negro',
  ),
  scenes: [
    { key: 'hook', name: SN.hook, duration: 1600, transition: { preset: 'glitch', duration: 400 } },
    {
      key: 'power',
      name: t('Training', 'Тренування', 'Тренировка', 'Trening', 'Antrenament', 'Entreno'),
      duration: 2400,
      transition: { preset: 'slices', duration: 700 },
    },
    {
      key: 'classes',
      name: t('Schedule', 'Розклад', 'Расписание', 'Grafik', 'Program', 'Horario'),
      duration: 3200,
      transition: { preset: 'flash', duration: 300 },
    },
    {
      key: 'offer',
      name: SN.offer,
      duration: 2400,
      transition: {
        preset: 'diagonal',
        duration: 800,
        params: { color: '#C6FF00', color2: '#0A0A0A', color3: '#FF2D87' },
      },
    },
    { key: 'cta', name: SN.cta, duration: 2600 },
  ],
  build(b) {
    const C = { black: '#0A0A0A', lime: '#C6FF00', pink: '#FF2D87', white: '#FFFFFF' };
    const F = b.F;
    const head: Partial<TextLayer> = {
      fontFamily: 'Russo One',
      fontWeight: 400,
      textTransform: 'uppercase',
      lineHeight: 0.95,
    };

    const s0 = b.sc('hook');
    b.backdrop(s0, 'hook_bg', rad('#1D1D1D', C.black));
    [0, 1, 2].forEach((i) =>
      b.shape(
        s0,
        `stripe_${i + 1}`,
        { x: 0, y: F.y + F.h * (0.08 + i * 0.05), w: b.W, h: b.s(0.025) },
        { shape: 'parallelogram', fill: solid(i === 1 ? C.pink : C.lime) },
        { enter: step('wipe', i * 80, 350, 'out-expo', { direction: i % 2 ? 'left' : 'right' }) },
      ),
    );
    b.text(
      s0,
      'headline',
      t('NO EXCUSES', 'БЕЗ ВІДМАЗОК', 'БЕЗ ОТГОВОРОК', 'BEZ WYMÓWEK', 'FĂRĂ SCUZE', 'SIN EXCUSAS'),
      b.in(0, 0.32, 1, 0.38),
      { ...head, fontSize: b.s(0.2), color: C.lime },
      { enter: step('glitch', 0, 500), emphasis: emph('shake', 900, 400) },
    );
    b.addFx(s0, 'glitch', 700, 400);
    b.addCamera(s0, 'shake', 150, 500, { intensity: 1.3 });

    const s1 = b.sc('power');
    b.photo(
      s1,
      'photo_power',
      'fitness',
      b.full(),
      { brightness: 0.85 },
      { keys: b.punchIn(1.3, 1.02, 1.1, 300) },
    );
    b.shade(s1, 'shade_power', b.full(), [
      [0, '#0A0A0A00'],
      [0.45, '#0A0A0A33'],
      [1, '#0A0A0AE6'],
    ]);
    b.text(
      s1,
      'train',
      t('TRAIN', 'ТРЕНУЙСЯ', 'ТРЕНИРУЙСЯ', 'TRENUJ', 'ANTRENEAZĂ-TE', 'ENTRENA'),
      b.in(0, 0.56, 1, 0.16),
      { ...head, fontSize: b.s(0.16), color: C.white },
      { enter: step('slide', 200, 400, 'out-expo', { direction: 'right', distance: 120 }) },
    );
    b.text(
      s1,
      'harder',
      t('HARDER', 'ЖОРСТКІШЕ', 'ЖЁСТЧЕ', 'MOCNIEJ', 'MAI TARE', 'MÁS DURO'),
      b.in(0, 0.74, 1, 0.18),
      {
        ...head,
        fontSize: b.s(0.16),
        color: C.black,
        highlight: { color: C.lime, paddingX: b.s(0.025), paddingY: 0, radius: 0 },
      },
      { enter: step('slide', 450, 400, 'out-expo', { direction: 'left', distance: 120 }) },
    );
    b.addCamera(s1, 'shake', 650, 450, { intensity: 1 });

    const s2 = b.sc('classes');
    b.backdrop(s2, 'classes_bg', solid(C.black));
    b.text(
      s2,
      'classes_title',
      t(
        'THIS WEEK',
        'ЦЬОГО ТИЖНЯ',
        'НА ЭТОЙ НЕДЕЛЕ',
        'W TYM TYGODNIU',
        'SĂPTĂMÂNA ASTA',
        'ESTA SEMANA',
      ),
      b.in(0, 0, 1, 0.12),
      { ...head, fontSize: b.s(0.09), color: C.pink, align: 'left' },
      { enter: step('glitch', 0, 400) },
    );
    const classes: Array<{ photo: PhotoId; name: Tx; when: Tx }> = [
      {
        photo: 'fitness',
        name: t('HIIT', 'HIIT', 'HIIT', 'HIIT', 'HIIT', 'HIIT'),
        when: t('Mon · 7:00', 'Пн · 7:00', 'Пн · 7:00', 'Pon · 7:00', 'Lun · 7:00', 'Lun · 7:00'),
      },
      {
        photo: 'swimming',
        name: t('SWIM', 'ПЛАВАННЯ', 'ПЛАВАНИЕ', 'BASEN', 'ÎNOT', 'NATACIÓN'),
        when: t(
          'Wed · 19:00',
          'Ср · 19:00',
          'Ср · 19:00',
          'Śr · 19:00',
          'Mie · 19:00',
          'Mié · 19:00',
        ),
      },
      {
        photo: 'yoga',
        name: t('YOGA', 'ЙОГА', 'ЙОГА', 'JOGA', 'YOGA', 'YOGA'),
        when: t(
          'Sat · 10:00',
          'Сб · 10:00',
          'Сб · 10:00',
          'Sob · 10:00',
          'Sâm · 10:00',
          'Sáb · 10:00',
        ),
      },
    ];
    const area = b.in(0, 0.16, 1, 0.84);
    const gap = b.s(0.025);
    const rh = (area.h - gap * 2) / 3;
    classes.forEach((c, i) => {
      const y = area.y + i * (rh + gap);
      const accent = i === 1 ? C.pink : C.lime;
      b.shape(
        s2,
        `class_bg_${i + 1}`,
        { x: area.x, y, w: area.w, h: rh },
        {
          shape: 'rectangle',
          fill: solid('#1A1A1A'),
          strokeWidth: Math.max(2, b.s(0.004)),
          strokeColor: accent,
          borderRadius: b.s(0.02),
        },
        {
          enter: step('slide', 200 + i * 180, 450, 'out-expo', {
            direction: i % 2 ? 'left' : 'right',
            distance: 60,
          }),
        },
      );
      const pw = Math.min(rh, area.w * 0.36);
      b.photo(
        s2,
        `class_photo_${i + 1}`,
        c.photo,
        { x: area.x, y, w: pw, h: rh },
        { borderRadius: b.s(0.02), brightness: 0.9 },
        {
          enter: step('slide', 200 + i * 180, 450, 'out-expo', {
            direction: i % 2 ? 'left' : 'right',
            distance: 60,
          }),
        },
      );
      const tx = area.x + pw + b.s(0.035);
      const tw = area.w - pw - b.s(0.06);
      b.text(
        s2,
        `class_name_${i + 1}`,
        c.name,
        { x: tx, y: y + rh * 0.14, w: tw, h: rh * 0.44 },
        { ...head, fontSize: b.s(0.08), color: accent, align: 'left' },
        { enter: step('glitch', 450 + i * 180, 350) },
      );
      b.text(
        s2,
        `class_time_${i + 1}`,
        c.when,
        { x: tx, y: y + rh * 0.58, w: tw, h: rh * 0.3 },
        {
          fontFamily: 'Inter',
          fontWeight: 800,
          fontSize: b.s(0.045),
          color: C.white,
          align: 'left',
        },
        { enter: step('fade', 550 + i * 180, 350) },
      );
    });

    const s3 = b.sc('offer');
    b.backdrop(s3, 'offer_bg', solid(C.pink));
    b.shape(
      s3,
      'offer_burst',
      b.centered(
        b.cx,
        F.y + F.h * 0.62,
        Math.min(F.w, F.h * 0.56, b.s(0.8)),
        Math.min(F.w, F.h * 0.56, b.s(0.8)),
      ),
      { shape: 'star', points: 14, innerRatio: 0.78, fill: solid(C.lime) },
      { enter: step('spin', 150, 500, 'out-back'), emphasis: emph('spin', 700, 9000, 1) },
    );
    b.text(
      s3,
      'offer_line',
      t(
        'FIRST CLASS',
        'ПЕРШЕ ЗАНЯТТЯ',
        'ПЕРВОЕ ЗАНЯТИЕ',
        'PIERWSZE ZAJĘCIA',
        'PRIMA ȘEDINȚĂ',
        'PRIMERA CLASE',
      ),
      b.in(0, 0.14, 1, 0.16),
      { ...head, fontSize: b.s(0.1), color: C.white },
      { enter: step('slide', 0, 400, 'out-expo', { direction: 'down', distance: 80 }) },
    );
    const free = b.text(
      s3,
      'offer',
      t('FREE', 'БЕЗКОШТОВНО', 'БЕСПЛАТНО', 'GRATIS', 'GRATUIT', 'GRATIS'),
      b.in(0, 0.5, 1, 0.24),
      { ...head, fontSize: b.s(0.22), color: C.black },
      { enter: step('zoom-out', 300, 400, 'out-expo'), emphasis: emph('shake', 900, 500, 2, 300) },
    );
    b.addFx(s3, 'burst', 400, 800, { anchor: free.id, color: C.lime, color2: C.white, size: 1.4 });
    b.addCamera(s3, 'zoom-burst', 300, 600, { intensity: 0.8 });

    const s4 = b.sc('cta');
    b.photo(
      s4,
      'photo_cta',
      'yoga',
      b.full(),
      { brightness: 0.5, grayscale: true },
      { keys: b.kenBurns(1.04, 1.12) },
    );
    b.shade(s4, 'cta_tint', b.full(), [
      [0, '#0A0A0A33'],
      [1, '#0A0A0AE6'],
    ]);
    b.text(
      s4,
      'cta_headline',
      t(
        'YOUR FIRST WEEK STARTS NOW',
        'ТВІЙ ПЕРШИЙ ТИЖДЕНЬ — ВЖЕ ЗАРАЗ',
        'ТВОЯ ПЕРВАЯ НЕДЕЛЯ — УЖЕ СЕЙЧАС',
        'TWÓJ PIERWSZY TYDZIEŃ TERAZ',
        'PRIMA TA SĂPTĂMÂNĂ ÎNCEPE ACUM',
        'TU PRIMERA SEMANA EMPIEZA YA',
      ),
      b.in(0, 0.2, 1, 0.32),
      { ...head, fontSize: b.s(0.1), color: C.white },
      { enter: step('words-rise', 0, 450), split: { by: 'word', stagger: 80 } },
    );
    b.chip(
      s4,
      'cta',
      t('Join now', 'Приєднатися', 'Присоединиться', 'Dołącz teraz', 'Înscrie-te', 'Únete ya'),
      { x: b.cx, y: F.y + F.h * 0.6, h: b.s(0.12), align: 'center' },
      {
        bg: C.lime,
        color: C.black,
        font: 'Russo One',
        weight: 400,
        upper: true,
        radius: b.s(0.015),
      },
      {
        enter: step('pop', 600, 400, 'out-back'),
        emphasis: emph('shake', 1300, 450, 0, 700, { intensity: 0.6 }),
      },
    );
    b.text(
      s4,
      'site',
      'ironlab.gym',
      b.in(0, 0.8, 1, 0.08),
      { fontFamily: 'Inter', fontWeight: 800, fontSize: b.s(0.045), color: C.lime },
      { enter: step('glitch', 1000, 400) },
    );
    return solid(C.black);
  },
};

/** Event countdown: 3-2-1 with rings, then a reveal with confetti. */
const launchCountdown: VideoTemplateDef = {
  id: 'launch-countdown',
  category: 'event',
  format: 'square',
  name: t(
    '3-2-1 launch',
    'Відлік 3-2-1',
    'Отсчёт 3-2-1',
    'Odliczanie 3-2-1',
    'Numărătoare 3-2-1',
    'Cuenta 3-2-1',
  ),
  description: t(
    '3-2-1 countdown with rings, then a big reveal and confetti',
    'Відлік 3-2-1 з кільцями, потім ефектне розкриття й конфеті',
    'Отсчёт 3-2-1 с кольцами, затем эффектное раскрытие и конфетти',
    'Odliczanie 3-2-1 z pierścieniami, potem odsłona i konfetti',
    'Numărătoare 3-2-1 cu inele, apoi dezvăluire și confetti',
    'Cuenta 3-2-1 con anillos, gran revelación y confeti',
  ),
  scenes: [
    {
      key: 'c3',
      name: t('3', '3', '3', '3', '3', '3'),
      duration: 900,
      transition: { preset: 'cut' },
    },
    {
      key: 'c2',
      name: t('2', '2', '2', '2', '2', '2'),
      duration: 900,
      transition: { preset: 'cut' },
    },
    {
      key: 'c1',
      name: t('1', '1', '1', '1', '1', '1'),
      duration: 900,
      transition: { preset: 'flash', duration: 300 },
    },
    {
      key: 'reveal',
      name: t('Reveal', 'Розкриття', 'Раскрытие', 'Odsłona', 'Dezvăluire', 'Revelación'),
      duration: 3200,
      transition: { preset: 'iris', duration: 700 },
    },
    {
      key: 'details',
      name: SN.details,
      duration: 3200,
      transition: { preset: 'zoom-out', duration: 500 },
    },
    { key: 'cta', name: SN.cta, duration: 2800 },
  ],
  build(b) {
    const C = {
      night: '#0B0420',
      purple: '#7C3AED',
      pink: '#FF4FD8',
      gold: '#FFC83D',
      white: '#FFFFFF',
    };
    const F = b.F;
    const display: Partial<TextLayer> = {
      fontFamily: 'Dela Gothic One',
      fontWeight: 400,
      lineHeight: 1,
      textTransform: 'uppercase',
    };
    const body: Partial<TextLayer> = { fontFamily: 'Onest', fontWeight: 600 };

    const counts = [
      { key: 'c3', n: '3', color: C.purple },
      { key: 'c2', n: '2', color: C.pink },
      { key: 'c1', n: '1', color: C.gold },
    ];
    counts.forEach((c) => {
      const sc = b.sc(c.key);
      b.backdrop(sc, `${c.key}_bg`, rad(c.color, C.night));
      b.text(
        sc,
        `get_ready_${c.n}`,
        t('Get ready', 'Готові?', 'Готовы?', 'Gotowi?', 'Pregătiți?', '¿Listos?'),
        b.in(0, 0.04, 1, 0.1),
        {
          ...body,
          fontWeight: 800,
          fontSize: b.s(0.045),
          color: C.white,
          textTransform: 'uppercase',
          letterSpacing: b.s(0.01),
        },
      );
      const num = b.text(
        sc,
        `count_${c.n}`,
        c.n,
        b.in(0, 0.16, 1, 0.72),
        { ...display, fontSize: b.s(0.62), color: C.white },
        { enter: step('zoom-out', 0, 320, 'out-expo'), exit: step('zoom-in', 0, 160, 'in-expo') },
      );
      b.addFx(sc, 'rings', 0, 900, { anchor: num.id, color: C.white, color2: c.color, count: 3 });
    });

    const s3 = b.sc('reveal');
    b.photo(s3, 'photo_reveal', 'fireworks', b.full(), {}, { keys: b.kenBurns(1.2, 1.04) });
    b.shape(s3, 'reveal_tint', b.full(), { fill: rad('#0B042033', '#0B0420CC') });
    b.text(
      s3,
      'headline',
      t(
        'GRAND OPENING',
        'ВЕЛИКЕ ВІДКРИТТЯ',
        'ГРАНДИОЗНОЕ ОТКРЫТИЕ',
        'WIELKIE OTWARCIE',
        'MAREA DESCHIDERE',
        'GRAN INAUGURACIÓN',
      ),
      b.in(0, 0.26, 1, 0.36),
      { ...display, fontSize: b.s(0.13), color: C.white },
      { enter: step('letters-pop', 0, 450), split: { by: 'char', stagger: 35 } },
    );
    b.chip(
      s3,
      'reveal_tag',
      t(
        'Live music · Free entry',
        'Жива музика · Вхід вільний',
        'Живая музыка · Вход свободный',
        'Muzyka na żywo · Wstęp wolny',
        'Muzică live · Intrare liberă',
        'Música en vivo · Entrada libre',
      ),
      { x: b.cx, y: F.y + F.h * 0.68, h: b.s(0.085), align: 'center' },
      { bg: C.gold, color: C.night, font: 'Onest', weight: 800 },
      { enter: step('pop', 1000, 450, 'out-back') },
    );
    b.addFx(s3, 'confetti', 200, 2600, {
      color: C.gold,
      color2: C.pink,
      color3: C.white,
      count: 90,
      y: 45,
    });
    b.addCamera(s3, 'zoom-burst', 0, 700);

    const s4 = b.sc('details');
    b.backdrop(s4, 'details_bg', lin(160, C.night, '#1E0B4A'));
    const card = b.pick<Box>({
      upright: b.in(0, 0, 1, 0.48),
      square: b.in(0, 0, 0.38, 1),
      wide: b.in(0, 0, 0.42, 1),
    });
    b.photo(
      s4,
      'photo_details',
      'guitarist',
      card,
      { borderRadius: b.s(0.04), borderWidth: b.s(0.006), borderColor: C.pink },
      { enter: step('pop', 0, 550, 'out-back'), keys: b.kenBurns(1, 1.06) },
    );
    const info = b.upright
      ? b.in(0, 0.54, 1, 0.46)
      : b.in(b.wide ? 0.48 : 0.44, 0.08, b.wide ? 0.52 : 0.56, 0.84);
    const rows: Array<{ icon: string; text: Tx; color: string }> = [
      {
        icon: 'calendar',
        text: t(
          'Sat, June 14',
          'Сб, 14 червня',
          'Сб, 14 июня',
          'Sob, 14 czerwca',
          'Sâm, 14 iunie',
          'Sáb, 14 de junio',
        ),
        color: C.gold,
      },
      {
        icon: 'clock',
        text: t(
          'Doors at 6 pm',
          'Початок о 18:00',
          'Начало в 18:00',
          'Start o 18:00',
          'Începe la 18:00',
          'Apertura a las 18:00',
        ),
        color: C.pink,
      },
      {
        icon: 'map-pin',
        text: t(
          'Central Park Stage',
          'Сцена в Центральному парку',
          'Сцена в Центральном парке',
          'Scena w Parku Centralnym',
          'Scena din Parcul Central',
          'Escenario del Parque Central',
        ),
        color: C.purple,
      },
    ];
    const rh = info.h / 3.4;
    rows.forEach((r, i) => {
      const y = info.y + i * rh * 1.15;
      const d = Math.min(rh * 0.8, b.s(0.1));
      b.shape(
        s4,
        `detail_dot_${i + 1}`,
        { x: info.x, y: y + (rh - d) / 2, w: d, h: d },
        { shape: 'ellipse', fill: solid(r.color) },
        { enter: step('pop', 300 + i * 200, 400, 'out-back') },
      );
      b.icon(
        s4,
        `detail_icon_${i + 1}`,
        r.icon,
        b.centered(info.x + d / 2, y + rh / 2, d * 0.5, d * 0.5),
        i === 0 ? C.night : C.white,
        {
          enter: step('pop', 350 + i * 200, 400, 'out-back'),
        },
      );
      b.text(
        s4,
        `detail_${i + 1}`,
        r.text,
        { x: info.x + d * 1.3, y, w: info.w - d * 1.3, h: rh },
        { ...body, fontWeight: 700, fontSize: b.s(0.05), color: C.white, align: 'left' },
        {
          enter: step('slide', 400 + i * 200, 500, 'out-expo', {
            direction: 'right',
            distance: 15,
          }),
        },
      );
    });

    const s5 = b.sc('cta');
    b.backdrop(s5, 'cta_bg', lin(135, C.purple, C.pink));
    b.text(
      s5,
      'cta_headline',
      t(
        'SAVE THE DATE',
        'ЗБЕРЕЖІТЬ ДАТУ',
        'СОХРАНИТЕ ДАТУ',
        'ZAPISZ DATĘ',
        'NOTEAZĂ DATA',
        'RESERVA LA FECHA',
      ),
      b.in(0, 0.18, 1, 0.34),
      { ...display, fontSize: b.s(0.13), color: C.white },
      { enter: step('words-flip', 0, 600), split: { by: 'word', stagger: 100 } },
    );
    b.chip(
      s5,
      'cta',
      t(
        'Get free tickets',
        'Отримати квитки',
        'Получить билеты',
        'Odbierz bilety',
        'Ia bilete gratuite',
        'Consigue entradas',
      ),
      { x: b.cx, y: F.y + F.h * 0.6, h: b.s(0.11), align: 'center' },
      { bg: C.gold, color: C.night, font: 'Onest', weight: 800 },
      { enter: step('pop', 600, 450, 'out-back'), emphasis: emph('tada', 1300, 900, 0, 500) },
    );
    b.text(
      s5,
      'site',
      'citylive.events',
      b.in(0, 0.8, 1, 0.08),
      { ...body, fontSize: b.s(0.04), color: C.white, opacity: 0.85 },
      { enter: step('fade', 1000, 400) },
    );
    b.addFx(
      s5,
      'shapes',
      0,
      2800,
      { color: C.gold, color2: C.white, color3: C.night, count: 12 },
      'back',
    );
    return solid(C.night);
  },
};

/** App promo in a phone mockup: device slides in, floating feature chips. */
const appPhone: VideoTemplateDef = {
  id: 'app-phone-mockup',
  category: 'tech',
  format: 'vertical',
  name: t(
    'App in a phone',
    'Застосунок у телефоні',
    'Приложение в телефоне',
    'Aplikacja w telefonie',
    'Aplicație în telefon',
    'App en un móvil',
  ),
  description: t(
    'Phone mockup slides in, floating feature chips and a store rating',
    'Макет телефона виїжджає, плаваючі чипи функцій і рейтинг',
    'Макет телефона выезжает, плавающие чипы функций и рейтинг',
    'Makieta telefonu wjeżdża, pływające funkcje i ocena w sklepie',
    'Machetă de telefon, etichete plutitoare și rating în magazin',
    'Maqueta de móvil, etiquetas flotantes y valoración en tienda',
  ),
  scenes: [
    {
      key: 'hook',
      name: SN.hook,
      duration: 2200,
      transition: { preset: 'zoom-in', duration: 500 },
    },
    {
      key: 'phone',
      name: t('App', 'Застосунок', 'Приложение', 'Aplikacja', 'Aplicația', 'App'),
      duration: 3000,
      transition: { preset: 'crossfade', duration: 400 },
    },
    {
      key: 'budget',
      name: t('Feature', 'Функція', 'Функция', 'Funkcja', 'Funcție', 'Función'),
      duration: 3200,
      transition: { preset: 'push', duration: 500, params: { direction: 'up' } },
    },
    {
      key: 'rating',
      name: t('Rating', 'Рейтинг', 'Рейтинг', 'Ocena', 'Rating', 'Valoración'),
      duration: 2400,
      transition: { preset: 'slide-over', duration: 500, params: { direction: 'left' } },
    },
    { key: 'cta', name: SN.cta, duration: 2600 },
  ],
  build(b) {
    const C = {
      bg1: '#0E1033',
      bg2: '#3A1C71',
      mint: '#3DF5C2',
      blue: '#5B8CFF',
      white: '#FFFFFF',
      ink: '#0A0A12',
      soft: '#C9CCF5',
    };
    const F = b.F;
    const font: Partial<TextLayer> = { fontFamily: 'Golos Text', fontWeight: 800 };
    const body: Partial<TextLayer> = { fontFamily: 'Golos Text', fontWeight: 500 };

    // Device geometry: below the copy on vertical videos, beside it elsewhere
    const ratio = 2.05;
    // On vertical videos the phone runs into the bottom band (no text there)
    const ph = b.pick({
      tall: Math.min(F.w * 0.6 * ratio, b.H * 0.94 - F.y - F.h * 0.36),
      portrait: F.h * 0.8,
      square: F.h * 0.82,
      wide: F.h * 0.94,
    });
    const pw = ph / ratio;
    const pcx = b.tall ? b.cx : F.x + F.w * (b.wide ? 0.7 : 0.76);
    const phone: Box = {
      x: pcx - pw / 2,
      y: b.tall ? F.y + F.h * 0.36 : F.y + (F.h - ph) / 2,
      w: pw,
      h: ph,
    };
    const copy: Box = b.tall
      ? b.in(0, 0, 1, 0.34)
      : { x: F.x, y: F.y + F.h * 0.08, w: phone.x - F.x - b.s(0.05), h: F.h * 0.52 };
    const align = b.tall ? 'center' : 'left';
    const bezel = pw * 0.045;
    const screen: Box = {
      x: phone.x + bezel,
      y: phone.y + bezel,
      w: pw - bezel * 2,
      h: ph - bezel * 2,
    };

    const device = (sc: SceneRef, suffix: string, slide: boolean) => {
      const keys = slide
        ? [
            { at: 0, y: Math.round(b.H * 0.7), easing: 'out-expo' },
            { at: 800, y: 0, easing: 'ease-in-out' },
            { at: -1, y: -b.s(0.01) },
          ]
        : [
            { at: 0, y: -b.s(0.01), easing: 'ease-in-out' },
            { at: -1, y: -b.s(0.02) },
          ];
      b.shape(
        sc,
        `phone_frame${suffix}`,
        phone,
        {
          fill: solid(C.ink),
          borderRadius: pw * 0.16,
          strokeWidth: Math.max(2, b.s(0.004)),
          strokeColor: '#3A3F7A',
          shadow: { x: 0, y: b.s(0.03), blur: b.s(0.08), color: '#00000080' },
        },
        { keys },
      );
      return keys;
    };
    const notch = (sc: SceneRef, suffix: string, keys: Array<{ at: number; y: number }>) =>
      b.shape(
        sc,
        `phone_notch${suffix}`,
        b.centered(pcx, screen.y + pw * 0.06, pw * 0.3, pw * 0.07),
        { fill: solid(C.ink), borderRadius: pw },
        { keys },
      );

    const s0 = b.sc('hook');
    const ic = b.s(0.16);
    const iconBox = b.tall
      ? b.centered(b.cx, F.y + F.h * 0.38, ic, ic)
      : { x: copy.x, y: copy.y, w: ic, h: ic };
    b.shape(
      s0,
      'app_icon',
      iconBox,
      {
        fill: lin(135, C.mint, C.blue),
        borderRadius: ic * 0.26,
        shadow: { x: 0, y: b.s(0.02), blur: b.s(0.05), color: '#3DF5C255' },
      },
      { enter: step('pop', 0, 550, 'spring-bouncy'), emphasis: emph('float', 800, 2400, 0) },
    );
    b.icon(
      s0,
      'app_icon_glyph',
      'wallet',
      b.centered(iconBox.x + ic / 2, iconBox.y + ic / 2, ic * 0.5, ic * 0.5),
      C.white,
      {
        enter: step('pop', 150, 500, 'spring-bouncy'),
        emphasis: emph('float', 800, 2400, 0),
      },
    );
    const hb = b.tall
      ? b.in(0, 0.48, 1, 0.3)
      : { x: copy.x, y: copy.y + ic * 1.3, w: F.w * 0.9, h: copy.h * 0.5 };
    b.text(
      s0,
      'headline',
      t(
        'Your money. Finally organized.',
        'Твої гроші. Нарешті в порядку.',
        'Твои деньги. Наконец в порядке.',
        'Twoje pieniądze. Wreszcie w porządku.',
        'Banii tăi. În sfârșit în ordine.',
        'Tu dinero. Por fin en orden.',
      ),
      hb,
      { ...font, fontSize: b.s(0.1), color: C.white, align, lineHeight: 1.05 },
      { enter: step('words-rise', 300, 550), split: { by: 'word', stagger: 90 } },
    );
    b.text(
      s0,
      'subline',
      t(
        'Budgets, savings and goals in one app',
        'Бюджет, заощадження й цілі в одному застосунку',
        'Бюджет, накопления и цели в одном приложении',
        'Budżet, oszczędności i cele w jednej aplikacji',
        'Buget, economii și obiective într-o aplicație',
        'Presupuesto, ahorro y metas en una app',
      ),
      { x: hb.x, y: hb.y + hb.h + b.s(0.02), w: hb.w, h: b.s(0.12) },
      { ...body, fontSize: b.s(0.042), color: C.soft, align },
      { enter: step('fade', 1100, 500) },
    );

    const s1 = b.sc('phone');
    b.text(
      s1,
      'phone_title',
      t(
        'Track every account in one place',
        'Усі рахунки в одному місці',
        'Все счета в одном месте',
        'Wszystkie konta w jednym miejscu',
        'Toate conturile într-un singur loc',
        'Todas tus cuentas en un lugar',
      ),
      copy,
      { ...font, fontSize: b.s(0.085), color: C.white, align, lineHeight: 1.08 },
      { enter: step('lines-mask', 300, 700), split: { by: 'line', stagger: 120 } },
    );
    const k1 = device(s1, '', true);
    b.photo(
      s1,
      'phone_screen',
      'trading-screen',
      screen,
      { borderRadius: pw * 0.12 },
      { keys: k1 },
    );
    notch(s1, '', k1);
    const chips: Array<{
      icon: string;
      text: Tx;
      bg: string;
      fg: string;
      x: number;
      y: number;
      delay: number;
    }> = [
      {
        icon: 'trending-up',
        text: t(
          '+24% savings',
          '+24% заощаджень',
          '+24% накоплений',
          '+24% oszczędności',
          '+24% economii',
          '+24% de ahorro',
        ),
        bg: C.mint,
        fg: C.ink,
        x: F.x,
        y: b.tall ? phone.y + ph * 0.22 : F.y + F.h * 0.66,
        delay: 900,
      },
      {
        icon: 'bell',
        text: t(
          'Smart alerts',
          'Розумні сповіщення',
          'Умные уведомления',
          'Inteligentne alerty',
          'Alerte inteligente',
          'Alertas inteligentes',
        ),
        bg: C.white,
        fg: C.ink,
        x: b.tall ? F.x + F.w * 0.42 : F.x,
        y: b.tall ? phone.y + ph * 0.5 : F.y + F.h * 0.8,
        delay: 1150,
      },
    ];
    chips.forEach((c, i) => {
      const h = b.s(0.085);
      const w = Math.min(b.textW(c.text, h * 0.4) + h * 1.6, b.tall ? F.x + F.w - c.x : copy.w);
      b.shape(
        s1,
        `feature_chip_${i + 1}`,
        { x: c.x, y: c.y, w, h },
        {
          fill: solid(c.bg),
          borderRadius: h / 2,
          shadow: { x: 0, y: b.s(0.015), blur: b.s(0.04), color: '#00000066' },
        },
        {
          enter: step('pop', c.delay, 450, 'spring-bouncy'),
          emphasis: emph('float', c.delay + 500, 2000, 0),
        },
      );
      b.icon(
        s1,
        `feature_icon_${i + 1}`,
        c.icon,
        { x: c.x + h * 0.28, y: c.y + h * 0.24, w: h * 0.52, h: h * 0.52 },
        c.fg,
        {
          enter: step('pop', c.delay, 450, 'spring-bouncy'),
          emphasis: emph('float', c.delay + 500, 2000, 0),
        },
      );
      b.text(
        s1,
        `feature_${i + 1}`,
        c.text,
        { x: c.x + h * 0.95, y: c.y, w: w - h * 1.2, h },
        {
          ...font,
          fontWeight: 700,
          fontSize: Math.round(h * 0.4),
          color: c.fg,
          align: 'left',
          maxLines: 1,
        },
        {
          enter: step('pop', c.delay, 450, 'spring-bouncy'),
          emphasis: emph('float', c.delay + 500, 2000, 0),
        },
      );
    });

    // Second feature: a screen drawn from shapes (balance card and a bar chart)
    const s2 = b.sc('budget');
    b.text(
      s2,
      'budget_title',
      t(
        'See where every dollar goes',
        'Бачиш, куди йде кожна гривня',
        'Видишь, куда уходит каждый рубль',
        'Widzisz, na co idzie każdy złoty',
        'Vezi unde merge fiecare leu',
        'Mira a dónde va cada euro',
      ),
      copy,
      { ...font, fontSize: b.s(0.085), color: C.white, align, lineHeight: 1.08 },
      { enter: step('lines-mask', 200, 700), split: { by: 'line', stagger: 120 } },
    );
    const k2 = device(s2, '_2', false);
    b.shape(
      s2,
      'screen_bg',
      screen,
      { fill: solid('#F4F6FF'), borderRadius: pw * 0.12 },
      { keys: k2 },
    );
    notch(s2, '_2', k2);
    const sp = screen.w * 0.08;
    const bal: Box = {
      x: screen.x + sp,
      y: screen.y + screen.h * 0.1,
      w: screen.w - sp * 2,
      h: screen.h * 0.22,
    };
    b.shape(
      s2,
      'balance_card',
      bal,
      { fill: lin(135, '#6D4AFF', C.blue), borderRadius: screen.w * 0.08 },
      {
        enter: step('rise', 200, 600, 'out-expo'),
        keys: k2,
      },
    );
    b.text(
      s2,
      'balance_label',
      t('Balance', 'Баланс', 'Баланс', 'Saldo', 'Sold', 'Saldo'),
      { x: bal.x + sp, y: bal.y + bal.h * 0.14, w: bal.w - sp * 2, h: bal.h * 0.24 },
      { ...body, fontSize: Math.round(screen.w * 0.055), color: '#FFFFFFCC', align: 'left' },
      { enter: step('fade', 400, 400), keys: k2 },
    );
    b.text(
      s2,
      'balance',
      money({ usd: 12480, uah: 512400, rub: 1124000, pln: 49900, ron: 57300, eur: 11590 }),
      { x: bal.x + sp, y: bal.y + bal.h * 0.4, w: bal.w - sp * 2, h: bal.h * 0.42 },
      {
        ...font,
        fontSize: Math.round(screen.w * 0.12),
        color: C.white,
        align: 'left',
        maxLines: 1,
      },
      { enter: step('blur-in', 500, 500), keys: k2 },
    );
    const chart: Box = {
      x: screen.x + sp,
      y: screen.y + screen.h * 0.42,
      w: screen.w - sp * 2,
      h: screen.h * 0.36,
    };
    b.text(
      s2,
      'chart_label',
      t(
        'This week',
        'Цього тижня',
        'На этой неделе',
        'Ten tydzień',
        'Săptămâna asta',
        'Esta semana',
      ),
      { x: chart.x, y: chart.y - screen.h * 0.06, w: chart.w, h: screen.h * 0.05 },
      {
        ...font,
        fontWeight: 700,
        fontSize: Math.round(screen.w * 0.06),
        color: '#1B1E4B',
        align: 'left',
      },
      { enter: step('fade', 500, 400), keys: k2 },
    );
    const vals = [0.45, 0.7, 0.38, 0.92, 0.6, 0.8];
    const bw = (chart.w / vals.length) * 0.62;
    vals.forEach((v, i) => {
      const h = chart.h * v;
      b.shape(
        s2,
        `bar_${i + 1}`,
        {
          x: chart.x + (chart.w / vals.length) * i + (chart.w / vals.length - bw) / 2,
          y: chart.y + chart.h - h,
          w: bw,
          h,
        },
        { fill: solid(i === 3 ? C.mint : '#C7CEF7'), borderRadius: bw * 0.3 },
        { enter: step('unfold', 600 + i * 90, 600, 'out-back', { direction: 'up' }), keys: k2 },
      );
    });
    b.shape(
      s2,
      'goal_pill',
      { x: chart.x, y: screen.y + screen.h * 0.84, w: chart.w, h: screen.h * 0.08 },
      { fill: solid('#E3E7FF'), borderRadius: screen.h },
      { enter: step('fade', 1100, 400), keys: k2 },
    );
    b.shape(
      s2,
      'goal_fill',
      { x: chart.x, y: screen.y + screen.h * 0.84, w: chart.w * 0.68, h: screen.h * 0.08 },
      { fill: lin(90, C.mint, C.blue), borderRadius: screen.h },
      { enter: step('wipe', 1300, 900, 'out-expo', { direction: 'right' }), keys: k2 },
    );

    const s3 = b.sc('rating');
    b.text(
      s3,
      'rating',
      '4.9',
      b.in(0, b.tall ? 0.12 : 0.06, 1, 0.34),
      { ...font, fontWeight: 900, fontSize: b.s(0.3), color: C.white },
      { enter: step('zoom-out', 0, 600, 'out-expo') },
    );
    const st = Math.min(b.s(0.1), F.w * 0.14);
    for (let i = 0; i < 5; i++)
      b.shape(
        s3,
        `star_${i + 1}`,
        b.centered(b.cx + (i - 2) * st * 1.2, F.y + F.h * (b.tall ? 0.5 : 0.46), st, st),
        { shape: 'star', points: 5, innerRatio: 0.45, fill: solid(C.mint) },
        { enter: step('pop', 300 + i * 100, 400, 'spring-bouncy') },
      );
    b.text(
      s3,
      'downloads',
      t(
        '1M+ happy users',
        '1 млн+ задоволених користувачів',
        '1 млн+ довольных пользователей',
        '1 mln+ zadowolonych użytkowników',
        '1 mil.+ utilizatori mulțumiți',
        '1 M+ usuarios felices',
      ),
      b.in(0, b.tall ? 0.6 : 0.6, 1, 0.12),
      { ...font, fontWeight: 700, fontSize: b.s(0.06), color: C.white },
      { enter: step('rise', 900, 500, 'out-expo') },
    );
    b.chip(
      s3,
      'award',
      t(
        'App of the day',
        'Застосунок дня',
        'Приложение дня',
        'Aplikacja dnia',
        'Aplicația zilei',
        'App del día',
      ),
      { x: b.cx, y: F.y + F.h * (b.tall ? 0.76 : 0.78), h: b.s(0.08), align: 'center' },
      { bg: '#FFFFFF1F', color: C.mint, font: 'Golos Text', weight: 700, upper: true },
      { enter: step('fade', 1200, 500) },
    );
    b.addFx(s3, 'rings', 300, 1400, {
      x: 50,
      y: 30,
      color: C.mint,
      color2: C.blue,
      count: 3,
      size: 0.8,
    });

    const s4 = b.sc('cta');
    const ic2 = b.s(0.18);
    b.shape(
      s4,
      'cta_icon',
      b.centered(b.cx, F.y + F.h * (b.tall ? 0.2 : 0.16), ic2, ic2),
      { fill: lin(135, C.mint, C.blue), borderRadius: ic2 * 0.26 },
      { enter: step('pop', 0, 500, 'spring-bouncy') },
    );
    b.icon(
      s4,
      'cta_icon_glyph',
      'wallet',
      b.centered(b.cx, F.y + F.h * (b.tall ? 0.2 : 0.16), ic2 * 0.5, ic2 * 0.5),
      C.white,
      {
        enter: step('pop', 100, 500, 'spring-bouncy'),
      },
    );
    b.text(
      s4,
      'cta_headline',
      t(
        'Start saving today',
        'Почни заощаджувати сьогодні',
        'Начни копить сегодня',
        'Zacznij oszczędzać już dziś',
        'Începe să economisești azi',
        'Empieza a ahorrar hoy',
      ),
      b.in(0, b.tall ? 0.33 : 0.32, 1, 0.22),
      { ...font, fontSize: b.s(0.1), color: C.white, lineHeight: 1.05 },
      { enter: step('words-rise', 250, 500), split: { by: 'word', stagger: 80 } },
    );
    b.chip(
      s4,
      'cta',
      t(
        'Download free',
        'Завантажити безкоштовно',
        'Скачать бесплатно',
        'Pobierz za darmo',
        'Descarcă gratuit',
        'Descárgala gratis',
      ),
      { x: b.cx, y: F.y + F.h * 0.6, h: b.s(0.11), align: 'center' },
      { bg: C.mint, color: C.ink, font: 'Golos Text', weight: 800 },
      { enter: step('pop', 700, 450, 'out-back'), emphasis: emph('pulse', 1300, 1100, 0) },
    );
    b.text(
      s4,
      'platforms',
      'iOS · Android · moneyflow.app',
      b.in(0, 0.76, 1, 0.08),
      { ...body, fontSize: b.s(0.038), color: C.soft },
      { enter: step('fade', 1000, 400) },
    );
    b.addFx(
      null,
      'aurora',
      0,
      b.duration,
      { color: C.bg2, color2: '#1B5E8A', color3: '#3DF5C2', opacity: 0.35 },
      'back',
    );
    return lin(160, C.bg1, C.bg2);
  },
};

export const TEMPLATES_B: VideoTemplateDef[] = [
  menu,
  propertyTour,
  cinematicTravel,
  fitnessHype,
  launchCountdown,
  appPhone,
];
